import React, { useEffect, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { useAuthStore } from './stores/authStore';
import ProtectedRoute from './components/auth/ProtectedRoute';
import LoginPage from './pages/LoginPage';
import DocumentEditor from './components/document/DocumentEditor';
import AssessmentEditor from './components/assessment/AssessmentEditor';
import AssessmentTaking from './components/assessment/AssessmentTaking';
import AssessmentResults from './components/assessment/AssessmentResults';
import AssessmentDocTaking from './components/document/AssessmentDocTaking';
import SharedDocumentPage from './pages/SharedDocumentPage';
import MarkingPage from './pages/MarkingPage';
import GradingInboxPage from './pages/GradingInboxPage';
import AnalysisPage from './pages/AnalysisPage';
import AdminPage from './pages/AdminPage';
import ToolsPage from './pages/ToolsPage';
import LearnersPage from './pages/LearnersPage';
import PricingPage from './pages/PricingPage';
import GuideSidebarShell from './components/guides/GuideSidebarShell';
import { PLAN_IDS } from './utils/subscription';
import { useDocumentStore } from './stores/documentStore';

// Route wrapper components to extract params
function DocumentEditorRoute() {
  const { id } = useParams();
  return <DocumentEditor documentId={id} />;
}

function DocumentEditorDebugRoute() {
  const initialDocument = useMemo(() => {
    try {
      return JSON.parse(window.sessionStorage.getItem('sigma.pagination.debugDocument') || 'null');
    } catch (_) {
      return null;
    }
  }, []);
  useEffect(() => {
    window.__SIGMA_DOCUMENT_STORE__ = useDocumentStore;
    return () => {
      delete window.__SIGMA_DOCUMENT_STORE__;
    };
  }, []);
  return <DocumentEditor documentId="new" debugInitialDocument={initialDocument} />;
}

function AssessmentDocTakingRoute() {
  const { id } = useParams();
  return <AssessmentDocTaking documentId={id} />;
}

function AssessmentEditorRoute() {
  const { id } = useParams();
  return <AssessmentEditor assessmentId={id} />;
}

function AssessmentTakingRoute() {
  const { id } = useParams();
  return <AssessmentTaking assessmentId={id} />;
}

function AssessmentResultsRoute() {
  const { id } = useParams();
  return <AssessmentResults assessmentId={id} />;
}

function MarkingJobRoute() {
  const { id } = useParams();
  return <MarkingPage jobId={id} />;
}

function ToolsPageRoute() {
  const { toolSlug, gemId } = useParams();
  return <ToolsPage toolSlug={toolSlug} gemId={gemId} />;
}

function withGuideSidebar(element) {
  return <GuideSidebarShell>{element}</GuideSidebarShell>;
}

function App() {
  const { initializeAuth, isLoading, setLoading } = useAuthStore();

  useEffect(() => {
    try {
      const unsubscribe = initializeAuth();
      return () => {
        if (typeof unsubscribe === 'function') unsubscribe();
      };
    } catch (error) {
      console.error('Firebase initialization failed:', error);
      setLoading(false);
    }
  }, [initializeAuth, setLoading]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sigma-600"></div>
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        <Route path="/login" element={<LoginPage />} />

        {process.env.NODE_ENV === 'development' && (
          <Route path="/__debug/document-editor" element={<DocumentEditorDebugRoute />} />
        )}

        <Route
          path="/"
          element={
            <ProtectedRoute>
              {withGuideSidebar(<DocumentEditor documentId="new" />)}
            </ProtectedRoute>
          }
        />

        <Route
          path="/document/new"
          element={
            <ProtectedRoute>
              {withGuideSidebar(<DocumentEditor documentId="new" />)}
            </ProtectedRoute>
          }
        />

        <Route
          path="/document/:id"
          element={
            <ProtectedRoute>
              {withGuideSidebar(<DocumentEditorRoute />)}
            </ProtectedRoute>
          }
        />

        <Route
          path="/document/:id/take"
          element={
            <ProtectedRoute>
              {withGuideSidebar(<AssessmentDocTakingRoute />)}
            </ProtectedRoute>
          }
        />

        <Route
          path="/assessment/new"
          element={
            <ProtectedRoute requiredPlan={PLAN_IDS.PRO}>
              {withGuideSidebar(<AssessmentEditor assessmentId="new" />)}
            </ProtectedRoute>
          }
        />

        <Route
          path="/assessment/:id"
          element={
            <ProtectedRoute requiredPlan={PLAN_IDS.PRO}>
              {withGuideSidebar(<AssessmentEditorRoute />)}
            </ProtectedRoute>
          }
        />

        <Route
          path="/assessment/:id/take"
          element={
            <ProtectedRoute>
              {withGuideSidebar(<AssessmentTakingRoute />)}
            </ProtectedRoute>
          }
        />

        <Route
          path="/assessment/:id/results"
          element={
            <ProtectedRoute>
              {withGuideSidebar(<AssessmentResultsRoute />)}
            </ProtectedRoute>
          }
        />

        <Route
          path="/marking"
          element={
            <ProtectedRoute requiredPlan={PLAN_IDS.PRO}>
              {withGuideSidebar(<MarkingPage />)}
            </ProtectedRoute>
          }
        />

        <Route
          path="/marking/:id"
          element={
            <ProtectedRoute requiredPlan={PLAN_IDS.PRO}>
              {withGuideSidebar(<MarkingJobRoute />)}
            </ProtectedRoute>
          }
        />

        <Route
          path="/grading"
          element={
            <ProtectedRoute requiredPlan={PLAN_IDS.PRO}>
              {withGuideSidebar(<GradingInboxPage />)}
            </ProtectedRoute>
          }
        />

        <Route
          path="/analysis"
          element={
            <ProtectedRoute requiredPlan={PLAN_IDS.PRO}>
              {withGuideSidebar(<AnalysisPage />)}
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin"
          element={
            <ProtectedRoute requiredRole="admin">
              {withGuideSidebar(<AdminPage />)}
            </ProtectedRoute>
          }
        />

        <Route path="/tools" element={withGuideSidebar(<ToolsPageRoute />)} />
        <Route path="/tools/react/:toolSlug" element={withGuideSidebar(<ToolsPageRoute />)} />
        <Route path="/tools/gem/:gemId" element={withGuideSidebar(<ToolsPageRoute />)} />
        <Route path="/pricing" element={withGuideSidebar(<PricingPage />)} />

        <Route
          path="/learners"
          element={
            <ProtectedRoute requiredPlan={PLAN_IDS.PRO}>
              {withGuideSidebar(<LearnersPage />)}
            </ProtectedRoute>
          }
        />

        <Route
          path="/shared/:token"
          element={withGuideSidebar(<SharedDocumentPage />)}
        />

        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
